// Phase 6 launch-readiness refactor — Revive flow.
//
// When the timer hits 0, if the player has revives banked from the
// `extra_revive` talent, consume one to restore +20s and trigger a flash.
// Otherwise call `onLose` with the run payload.
import { useEffect, useState } from "react";

const REVIVE_TIME = 20; // seconds restored per revive

/**
 * @param {Object} args
 * @param {Object} args.endedRef           short-circuits after run end
 * @param {number} args.timeLeft           live timer
 * @param {Function} args.setTimeLeft      timer setter
 * @param {Object} args.revivesRef         App-scoped revive counter
 * @param {Function} args.stopBeam         visual cleanup
 * @param {Function} args.playLose         SFX
 * @param {Function} args.onLose           App-level callback
 * @param {Object} args.runStartRef        ref of run start timestamp (perf.now)
 * @param {Object} args.bossesKilledRef    ref counting boss kills
 * @param {Object} args.payloadRef         ref returning { score, coinsEarned, zombiesCollected }
 */
export function useRevives({
  endedRef,
  timeLeft,
  setTimeLeft,
  revivesRef,
  stopBeam,
  playLose,
  onLose,
  runStartRef,
  bossesKilledRef,
  payloadRef,
}) {
  const [reviveFlash, setReviveFlash] = useState(0);

  useEffect(() => {
    if (endedRef.current) return;
    if (timeLeft > 0) return;
    // Try to consume a revive first.
    if (revivesRef && revivesRef.current > 0) {
      revivesRef.current -= 1;
      setTimeLeft(REVIVE_TIME);
      setReviveFlash((n) => n + 1);
      return;
    }
    endedRef.current = true;
    stopBeam();
    playLose();
    const playSeconds = Math.round(
      (performance.now() - runStartRef.current) / 1000
    );
    const { score, coinsEarned, zombiesCollected } = payloadRef.current || {};
    setTimeout(
      () =>
        onLose({
          score,
          coinsEarned,
          zombiesCollected,
          bossesDefeated: bossesKilledRef.current,
          playSeconds,
        }),
      400
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  return { reviveFlash };
}
